import Image from "next/image"
import { FaApple, FaDocker, FaFigma, FaGithub } from "react-icons/fa6"
import {
    SiAwwwards,
    SiBiome,
    SiClaude,
    SiFramer,
    SiGooglegemini,
    SiLinear,
    SiNotion,
    SiRaycast,
    SiSupabase,
    SiVercel,
    SiWarp,
} from "react-icons/si"
import {
    AceternityIcon,
    AffinityIcon,
    AntigravityIcon,
    ArcIcon,
    BrunoIcon,
    CanvaIcon,
    CapIcon,
    CloudflareIcon,
    CobaltIcon,
    CodexIcon,
    CoolorsIcon,
    CursorIcon,
    DiaIcon,
    DribbbleIcon,
    ElevenLabsIcon,
    EmilSkillsIcon,
    FontshareIcon,
    FontsNinjaIcon,
    GhosttyIcon,
    GoogleTranslateIcon,
    GridLinesRulerIcon,
    HandyIcon,
    ICloudPasswordsIcon,
    InsporaIcon,
    JSONForgeIcon,
    LogoipsumIcon,
    MaccyIcon,
    MagicUIIcon,
    MobbinIcon,
    MoleIcon,
    MosIcon,
    MotionDevIcon,
    MotionPrimitivesIcon,
    NvidiaIcon,
    OpenAIIcon,
    OpenCodeIcon,
    OpenRouterIcon,
    PinterestIcon,
    ProtonVPNIcon,
    ReactBitsIcon,
    RelumeIcon,
    SentryIcon,
    ShottrIcon,
    SkillsShIcon,
    SoroIcon,
    StatsIcon,
    SVGLIcon,
    TLDrawIcon,
    TwentyFirstIcon,
    UBlockOriginIcon,
    WappalyzerIcon,
    ZAIIcon,
    ZedIcon,
} from "@/components/ui/tool-icons"
import { cn } from "@/lib/utils"
import type { SvglRoute } from "@/types/resources"

interface ResourceIconProps {
    svglRoute?: SvglRoute
    iconKey?: string
    title: string
    className?: string
}

export function ResourceIcon({
    svglRoute,
    iconKey,
    title,
    className,
}: ResourceIconProps) {
    const iconClass =
        "size-5 transition-transform duration-200 group-hover:scale-105"
    const appIconClass =
        "size-[22px] rounded-[4px] object-contain transition-transform duration-200 group-hover:scale-105"
    const circleIconClass =
        "size-[22px] rounded-full object-contain transition-transform duration-200 group-hover:scale-105"
    const moleClass =
        "h-[15px] w-[23px] object-contain transition-transform duration-200 group-hover:scale-105 dark:brightness-0 dark:invert"
    const motionPrimitivesClass =
        "h-[15px] w-[22px] object-contain transition-transform duration-200 group-hover:scale-105 dark:invert"

    const renderIcon = () => {
        // Priority 1: Dedicated macOS app icons and custom styled assets
        switch (iconKey) {
            case "arc":
                return <ArcIcon className={appIconClass} />
            case "dia":
                return <DiaIcon className={appIconClass} />
            case "antigravity":
                return <AntigravityIcon className={appIconClass} />
            case "handy":
                return <HandyIcon className={appIconClass} />
            case "mos":
                return <MosIcon className={appIconClass} />
            case "mole":
                return <MoleIcon className={moleClass} />
            case "shottr":
                return <ShottrIcon className={appIconClass} />
            case "stats":
                return <StatsIcon className={appIconClass} />
            case "maccy":
                return <MaccyIcon className={appIconClass} />
            case "cap":
                return <CapIcon className={iconClass} />
            case "coolors":
                return <CoolorsIcon className={appIconClass} />
            case "motion":
                return <MotionDevIcon className={appIconClass} />
            case "motion-primitives":
                return (
                    <MotionPrimitivesIcon className={motionPrimitivesClass} />
                )
            case "reactbits":
                return <ReactBitsIcon className={circleIconClass} />
            case "magicui":
                return <MagicUIIcon className={circleIconClass} />
            case "aceternity":
                return <AceternityIcon className={appIconClass} />
            case "21st":
                return <TwentyFirstIcon className={appIconClass} />
            case "emil-skills":
                return <EmilSkillsIcon className={appIconClass} />
            case "skills-sh":
                return <SkillsShIcon className={circleIconClass} />
            case "logosystem":
                return (
                    <Image
                        src="/icons/logosystem.png"
                        alt=""
                        width={22}
                        height={22}
                        unoptimized
                        className={appIconClass}
                    />
                )
            case "siteinspire":
                return (
                    <Image
                        src="/icons/siteinspire.svg"
                        alt=""
                        width={22}
                        height={22}
                        unoptimized
                        className={circleIconClass}
                    />
                )
            case "apple-design-awards":
                return (
                    <Image
                        src="/icons/apple-design-awards.svg"
                        alt=""
                        width={22}
                        height={22}
                        unoptimized
                        className={circleIconClass}
                    />
                )
            case "stitch":
                return (
                    <Image
                        src="/icons/stitch.svg"
                        alt=""
                        width={22}
                        height={22}
                        unoptimized
                        className={appIconClass}
                    />
                )
            case "fonts-ninja":
                return <FontsNinjaIcon className={appIconClass} />
            case "google-translate":
                return <GoogleTranslateIcon className={appIconClass} />
            case "grid-lines-ruler":
                return <GridLinesRulerIcon className={appIconClass} />
            case "proton-vpn":
                return <ProtonVPNIcon className={appIconClass} />
            case "wappalyzer":
                return <WappalyzerIcon className={appIconClass} />
            case "icloud-passwords":
                return <ICloudPasswordsIcon className={appIconClass} />
            case "ublock-origin":
                return <UBlockOriginIcon className={appIconClass} />
            case "cobalt":
                return <CobaltIcon className={appIconClass} />
            case "affinity":
                return <AffinityIcon className={appIconClass} />
            case "bruno":
                return <BrunoIcon className={iconClass} />
            case "soro":
                return <SoroIcon className={appIconClass} />
            case "json-forge":
                return <JSONForgeIcon className={appIconClass} />
            case "mobbin":
                return <MobbinIcon className={appIconClass} />
        }

        // Priority 2: SVGL routes (authentic colored SVGs from svgl.app or local SVG routes)
        if (svglRoute) {
            if (typeof svglRoute === "string") {
                return (
                    <Image
                        src={svglRoute}
                        alt=""
                        width={20}
                        height={20}
                        unoptimized
                        className="size-5 object-contain transition-transform duration-200 group-hover:scale-105"
                    />
                )
            }
            return (
                <>
                    <Image
                        src={svglRoute.light}
                        alt=""
                        width={20}
                        height={20}
                        unoptimized
                        className="size-5 object-contain transition-transform duration-200 group-hover:scale-105 dark:hidden"
                    />
                    <Image
                        src={svglRoute.dark}
                        alt=""
                        width={20}
                        height={20}
                        unoptimized
                        className="hidden size-5 object-contain transition-transform duration-200 group-hover:scale-105 dark:block"
                    />
                </>
            )
        }

        // Priority 3: Vector icon glyphs
        switch (iconKey) {
            case "awwwards":
                return <SiAwwwards className={iconClass} aria-hidden="true" />
            case "cursor":
                return <CursorIcon className={iconClass} />
            case "claude":
                return <SiClaude className={iconClass} aria-hidden="true" />
            case "zed":
                return <ZedIcon className={iconClass} />
            case "ghostty":
                return <GhosttyIcon className={iconClass} />
            case "warp":
                return <SiWarp className={iconClass} aria-hidden="true" />
            case "biome":
                return <SiBiome className={iconClass} aria-hidden="true" />
            case "cloudflare":
                return <CloudflareIcon className={iconClass} />
            case "vercel":
                return <SiVercel className={iconClass} aria-hidden="true" />
            case "supabase":
                return <SiSupabase className={iconClass} aria-hidden="true" />
            case "docker":
                return <FaDocker className={iconClass} aria-hidden="true" />
            case "opencode":
                return <OpenCodeIcon className={iconClass} />
            case "codex":
                return <CodexIcon className={iconClass} />
            case "elevenlabs":
                return <ElevenLabsIcon className={iconClass} />
            case "inspora":
                return <InsporaIcon className={iconClass} />
            case "openrouter":
                return <OpenRouterIcon className={iconClass} />
            case "nvidia":
                return <NvidiaIcon className={iconClass} />
            case "gemini":
                return (
                    <SiGooglegemini className={iconClass} aria-hidden="true" />
                )
            case "openai":
                return <OpenAIIcon className={iconClass} />
            case "zai":
                return <ZAIIcon className={iconClass} />
            case "figma":
                return <FaFigma className={iconClass} aria-hidden="true" />
            case "tldraw":
                return <TLDrawIcon className={iconClass} />
            case "canva":
                return <CanvaIcon className={iconClass} />
            case "dribbble":
                return <DribbbleIcon className={iconClass} />
            case "pinterest":
                return <PinterestIcon className={iconClass} />
            case "sentry":
                return <SentryIcon className={iconClass} />
            case "raycast":
                return <SiRaycast className={iconClass} aria-hidden="true" />
            case "linear":
                return <SiLinear className={iconClass} aria-hidden="true" />
            case "notion":
                return <SiNotion className={iconClass} aria-hidden="true" />
            case "framer":
                return <SiFramer className={iconClass} aria-hidden="true" />
            case "apple":
                return <FaApple className={iconClass} aria-hidden="true" />
            case "github":
                return <FaGithub className={iconClass} aria-hidden="true" />
            case "svgl":
                return <SVGLIcon className={iconClass} />
            case "logoipsum":
                return <LogoipsumIcon className={iconClass} />
            case "fontshare":
                return <FontshareIcon className={iconClass} />
            case "relume":
                return <RelumeIcon className={iconClass} />
            case "uncut":
                return (
                    <Image
                        src="/icons/uncut.svg"
                        alt=""
                        width={20}
                        height={20}
                        unoptimized
                        className={iconClass}
                    />
                )
            default:
                return (
                    <span className="flex size-6 items-center justify-center rounded border border-border/60 bg-muted/40 font-mono font-semibold text-[11px] text-muted-foreground uppercase transition-colors group-hover:text-foreground">
                        {title.slice(0, 2)}
                    </span>
                )
        }
    }

    return (
        <div
            className={cn(
                "flex size-6 shrink-0 items-center justify-center",
                className
            )}
        >
            {renderIcon()}
        </div>
    )
}
